import { useState, useEffect, useCallback, useRef } from 'react';
import { notify } from '../../../../utils/toast';
import { supabase } from '../../../../lib/supabase';
import {
    getInstagramConnections,
    saveInstagramIntegration,
    disconnectInstagramIntegration,
    refreshInstagramProfile,
    isValidInstagramUsername,
    type InstagramConnectionStatus,
} from '../../../../services/modules/instagramRepository';
import {
    clearInstagramOAuthSession,
    hasPendingInstagramOAuthCallback,
} from '../../../../services/modules/instagramSDK';
import { useInstagramAuthFlow, clearInstagramModalStash } from './useInstagramAuthFlow';
import type { InstagramAccountFromApi } from '../../../../services/modules/instagramService';

export interface UseInstagramConnectionReturn {
    connections: InstagramConnectionStatus[];
    isLoading: boolean;
    isConnecting: boolean;
    isSaving: boolean;
    isModalOpen: boolean;
    accounts: InstagramAccountFromApi[];
    connect: () => Promise<void>;
    disconnect: (integrationId?: string) => Promise<void>;
    save: (account: InstagramAccountFromApi) => Promise<void>;
    closeModal: () => void;
    refresh: () => Promise<void>;
}

export function useInstagramConnection(organizationId: string | undefined): UseInstagramConnectionReturn {
    const {
        isConnecting,
        authModalData,
        startAuthFlow,
        checkPendingAuth,
        clearAuthData,
    } = useInstagramAuthFlow();

    const [connections, setConnections] = useState<InstagramConnectionStatus[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const oauthResumedRef = useRef(false);

    const isModalOpen = authModalData !== null;
    const accounts = authModalData?.accounts ?? [];

    const refresh = useCallback(async () => {
        if (!organizationId) {
            setConnections([]);
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        try {
            let list = await getInstagramConnections(organizationId);
            const active = list.find((c) => c.connected);
            const needsUsername = active && !isValidInstagramUsername(active.instagramUsername);
            if (needsUsername) {
                const username = await refreshInstagramProfile(organizationId);
                if (username) {
                    list = await getInstagramConnections(organizationId);
                }
            }
            setConnections(list);
        } catch (error) {
            console.error('[useInstagramConnection] load error:', error);
        } finally {
            setIsLoading(false);
        }
    }, [organizationId]);

    useEffect(() => {
        if (!organizationId) {
            setConnections([]);
            setIsLoading(false);
            return;
        }

        if (hasPendingInstagramOAuthCallback()) {
            if (!oauthResumedRef.current) {
                oauthResumedRef.current = true;
                void startAuthFlow(organizationId).then(() => refresh());
            }
            return;
        }

        refresh();
        checkPendingAuth();
    }, [organizationId, refresh, checkPendingAuth, startAuthFlow]);

    const connect = useCallback(async () => {
        if (!organizationId) return;
        if (connections.some((c) => c.connected)) {
            notify.error('Já existe um Instagram conectado. Desconecte antes de conectar outro.');
            return;
        }
        await startAuthFlow(organizationId);
    }, [organizationId, startAuthFlow, connections]);

    const save = useCallback(async (account: InstagramAccountFromApi) => {
        if (!organizationId || !authModalData) return;
        if (connections.some((c) => c.connected)) {
            notify.error('Já existe um Instagram conectado nesta organização.');
            return;
        }

        setIsSaving(true);
        try {
            const expiresAt = authModalData.expiresIn
                ? new Date(Date.now() + authModalData.expiresIn * 1000).toISOString()
                : new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString();

            await saveInstagramIntegration(
                {
                    pageId: account.pageId,
                    pageName: account.pageName,
                    instagramBusinessAccountId: account.instagramBusinessAccountId,
                    instagramUsername: account.instagramUsername,
                    accessToken: authModalData.accessToken,
                    expiresAt,
                },
                organizationId
            );

            clearAuthData();
            clearInstagramModalStash();
            await refresh();
            notify.success('Instagram Direct conectado com sucesso!');
        } catch (error: unknown) {
            notify.error(error instanceof Error ? error.message : 'Erro ao salvar conexão');
        } finally {
            setIsSaving(false);
        }
    }, [organizationId, authModalData, clearAuthData, refresh]);

    // Instagram Login retorna 1 conta — conecta direto sem modal de seleção
    useEffect(() => {
        if (!authModalData || accounts.length !== 1 || isSaving) return;
        if (connections.some((c) => c.connected)) return;
        void save(accounts[0]);
    }, [authModalData, accounts, isSaving, connections, save]);

    const disconnect = useCallback(async () => {
        if (!organizationId) return;

        const confirmed = await notify.confirm('Desconectar Instagram?', {
            description: 'Mensagens do Direct deixarão de ser recebidas.',
            confirmText: 'Desconectar',
            cancelText: 'Cancelar',
        });
        if (!confirmed) return;

        try {
            const { data } = await supabase
                .from('integrations')
                .select('id')
                .eq('organization_id', organizationId)
                .eq('type', 'instagram_dm')
                .eq('status', 'connected')
                .maybeSingle();

            if (data?.id) {
                await disconnectInstagramIntegration(organizationId, data.id);
            }
            clearInstagramOAuthSession();
            clearAuthData();
            await refresh();
            notify.success('Instagram desconectado');
        } catch (error: unknown) {
            notify.error(error instanceof Error ? error.message : 'Erro ao desconectar');
        }
    }, [organizationId, connections, clearAuthData, refresh]);

    const closeModal = useCallback(() => {
        clearAuthData();
        clearInstagramOAuthSession();
    }, [clearAuthData]);

    return {
        connections,
        isLoading,
        isConnecting,
        isSaving,
        isModalOpen,
        accounts,
        connect,
        disconnect,
        save,
        closeModal,
        refresh,
    };
}
